import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Heart } from 'lucide-react';
import { renewalAPI } from '../lib/api';

export default function RenewalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [alreadyDone, setAlreadyDone] = useState(false);

  useEffect(() => {
    renewalAPI.getPage(token)
      .then(r => {
        setData(r.data);
        if (r.data.confirmed) setAlreadyDone(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await renewalAPI.confirmIntent(token, { indicative_amount: amount || null });
      setDone(true);
    } catch (e) {
      if (e.response?.status === 400) setAlreadyDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  const style = {
    page: { minHeight: '100vh', background: '#0D1117', color: '#e2e8f0', fontFamily: 'DM Sans, sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' },
    card: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '48px 40px', maxWidth: 520, width: '100%' },
    logo: { fontFamily: 'Syne, sans-serif', fontSize: '1.1rem', color: '#C9A84C', marginBottom: 32, textAlign: 'center', letterSpacing: '0.05em' },
    h1: { fontFamily: 'Syne, sans-serif', fontSize: '1.8rem', fontWeight: 700, color: '#f1f5f9', marginBottom: 12, lineHeight: 1.3 },
    p: { color: '#94a3b8', lineHeight: 1.8, fontSize: '0.9rem', marginBottom: 24 },
    label: { display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: 8, fontWeight: 500 },
    input: { width: '100%', padding: '12px 14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#e2e8f0', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' },
    btn: { width: '100%', padding: '14px', background: '#C9A84C', border: 'none', borderRadius: 8, color: '#0D1117', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', marginTop: 20, fontFamily: 'Syne, sans-serif' },
    btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
    hint: { fontSize: '0.75rem', color: '#4b5563', marginTop: 8, lineHeight: 1.6 },
  };

  if (loading) return (
    <div style={style.page}>
      <div style={{ color: '#2A9D8F' }}>Loading…</div>
    </div>
  );

  if (notFound) return (
    <div style={style.page}>
      <div style={style.card}>
        <div style={style.logo}>FUTURUM INNOVATION AWARDS</div>
        <h1 style={{ ...style.h1, color: '#ef4444' }}>Link not found</h1>
        <p style={style.p}>This renewal link is invalid or has expired. Please contact <a href="mailto:admin@futurumawards.com" style={{ color: '#2A9D8F' }}>admin@futurumawards.com</a> if you believe this is an error.</p>
        <Link to="/" style={{ color: '#2A9D8F', fontSize: '0.875rem', textDecoration: 'none' }}>← Back to Futurum</Link>
      </div>
    </div>
  );

  if (done || alreadyDone) return (
    <div style={style.page}>
      <div style={{ ...style.card, textAlign: 'center' }}>
        <div style={style.logo}>FUTURUM INNOVATION AWARDS</div>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(42,157,143,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
          <CheckCircle size={32} style={{ color: '#2A9D8F' }} />
        </div>
        <h1 style={style.h1}>{alreadyDone && !done ? 'Already confirmed' : 'Thank you!'}</h1>
        <p style={style.p}>
          {alreadyDone && !done
            ? 'You have already expressed your interest in returning for the next season. We will be in touch.'
            : `Your interest in sponsoring ${data?.season_to || 'the next season'} has been recorded. We will contact you when topics are open for sponsorship.`
          }
        </p>
        <a href="https://futurumawards.com" style={{ color: '#C9A84C', fontSize: '0.875rem', textDecoration: 'none' }}>Visit futurumawards.com →</a>
      </div>
    </div>
  );

  return (
    <div style={style.page}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700&family=DM+Sans:wght@400;500&display=swap" rel="stylesheet" />

      <div style={style.card}>
        <div style={style.logo}>FUTURUM INNOVATION AWARDS</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <Heart size={18} style={{ color: '#C9A84C', flexShrink: 0 }} />
          <span style={{ fontSize: '0.8rem', color: '#C9A84C', fontFamily: 'DM Mono, monospace', letterSpacing: '0.05em' }}>
            {data.season_to} — Sponsor Invitation
          </span>
        </div>

        <h1 style={style.h1}>Welcome back, {data.first_name}.</h1>

        <p style={style.p}>
          Thank you for your generous support during <strong style={{ color: '#e2e8f0' }}>{data.program_name} {data.season_from}</strong>.
          Your sponsorship made a real difference to a student's final year.
        </p>

        <p style={style.p}>
          We are now planning <strong style={{ color: '#e2e8f0' }}>{data.season_to}</strong> and would love to have you back.
          Express your interest below — no payment is required at this stage. We will contact you when research topics are ready for sponsorship.
        </p>

        <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 10, padding: '16px 18px', marginBottom: 28 }}>
          <div style={{ fontSize: '0.78rem', color: '#C9A84C', fontWeight: 500, marginBottom: 4 }}>Your {data.season_from} contribution</div>
          <div style={{ fontSize: '0.82rem', color: '#94a3b8' }}>
            {data.indicative_amount
              ? `You previously indicated ₦${Number(data.indicative_amount).toLocaleString()}`
              : 'You funded student research at Obafemi Awolowo University.'}
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <label style={style.label}>Indicative pledge amount for {data.season_to} (optional)</label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#C9A84C', fontWeight: 600 }}>₦</span>
            <input
              type="number" min="100000" step="50000"
              placeholder="e.g. 150000"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              style={{ ...style.input, paddingLeft: 28 }}
            />
          </div>
          <div style={style.hint}>Minimum ₦100,000 · increments of ₦50,000 · this is not a binding commitment</div>
        </div>

        <button
          onClick={handleConfirm}
          disabled={submitting}
          style={{ ...style.btn, ...(submitting ? style.btnDisabled : {}) }}
        >
          {submitting ? 'Confirming…' : `Yes, I'm in for ${data.season_to}`}
        </button>

        <div style={{ marginTop: 20, textAlign: 'center', fontSize: '0.75rem', color: '#4b5563', lineHeight: 1.6 }}>
          This link is unique to you. Questions? <a href="mailto:admin@futurumawards.com" style={{ color: '#2A9D8F', textDecoration: 'none' }}>admin@futurumawards.com</a>
        </div>
      </div>
    </div>
  );
}
